import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, Check, Images, ShoppingBag, Plus, ChevronLeft,
  Wind, Waves, Users, Flame, Sparkles, Utensils, Snowflake, Bed, Leaf, Moon, ChefHat, Music, CreditCard,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import QuantityStepper from "../components/QuantityStepper";
import AccommodationModal from "../components/AccommodationModal";
import CheckoutView from "../components/CheckoutView";
import BasketModal from "../components/BasketModal";
import SeoMeta from "../components/SeoMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLUG = "clarity-reset";
const gbp = (n) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(Number(n) || 0);
const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const HERO_SLIDES = [
  "/retreat-group.webp", "/retreat-c1.webp", "/retreat-c3.webp", "/retreat-c4.webp", "/retreat-c2.webp",
];

const METHOD = [
  { t: "GO WITHIN", d: "Slow down the noise and create space to actually hear yourself. Guided workshops, reflection and breathwork with Calum. This is the honest inner work most entrepreneurs and creatives never make time for." },
  { t: "REGULATE", d: "Get out of your head and back into your body. Sound baths, movement, breath, rest and the rustic spa, plus familiar guests from FERAL Fest leading regulate sessions. The nervous system reset that lets you think, create and lead clearly." },
  { t: "RECONNECT", d: "Reconnect with yourself and with a room full of ambitious, like minded rebels. Real conversations, shared meals, communal sea dips and the kind of networking that actually goes deep. The people you meet here could change your year." },
  { t: "REWILD", d: "Move beyond the patterns, fear and self doubt that keep your life and your ideas small. Workshops, breathwork and immersive experiences with Calum to help you access something bolder." },
  { t: "RISE", d: "Get clear on what you actually want in life and in business, and what you're going to do about it. Tom leads a powerful session on vision, possibility and unlocking your wildest potential." },
];

const SIDE_EFFECTS = [
  "Delusional levels of confidence and self-belief.",
  "Going after bigger, bolder goals in business and life you once thought impossible.",
  "An abundance of self-love.",
  "Finding your FERAL tribe, lifelong friends and future collaborators.",
  "Fresh perspectives, breakthroughs and clarity on your business and life challenges.",
  "A refusal to settle for anything less than what you really want.",
];

const INCLUDED = [
  { i: Bed, t: "3 nights accommodation" }, { i: Utensils, t: "All meals" }, { i: ChefHat, t: "Private chef" },
  { i: Sparkles, t: "FERAL workshops" }, { i: Wind, t: "Breathwork with Calum" }, { i: ArrowRight, t: "RISE with Tom" },
  { i: Music, t: "Sound baths" }, { i: Waves, t: "Movement" }, { i: Flame, t: "Rustic spa" },
  { i: Snowflake, t: "Hot tub + sea dips" }, { i: Leaf, t: "Nature" }, { i: Moon, t: "Time to switch off" },
];

const ITINERARY = [
  {
    day: "Friday",
    theme: "Arrive & Connect",
    items: [
      "Arrival & settle in",
      "Welcome to the retreat",
      "Regulate Evening Supper Club",
      "FERAL Fire: connection around the Roundhouse firepit",
    ],
  },
  {
    day: "Saturday",
    theme: "Reconnect & Rewild",
    items: [
      "Optional Regulate movement class",
      "Sacred Morning Sips & breakfast",
      "Reconnect with Calum: workshop",
      "Hearty lunch",
      "Reconnect: rustic spa therapy",
      "Reflect & Restore: hot tub, journal, wander or simply switch off",
      "Rustic Evening Fest: food, music, fire & connection",
      "Reconnect workshop with Tom, fireside",
    ],
  },
  {
    day: "Sunday",
    theme: "Regulate, Reflect & Rise",
    items: [
      "Optional sea dip",
      "Sacred Morning Sips & breakfast",
      "Expansive soundbath",
      "Lunch",
      "Rewild with Calum: breathwork",
      "Reflect & Restore: time for you",
      "Evening meal",
      "Rise with Tom: fireside workshop & closing circle",
    ],
  },
  {
    day: "Monday",
    theme: "Return",
    items: [
      "Optional sea dip",
      "Sacred Morning Sips & breakfast",
      "Optional coastal hike",
      "Pub lunch (not included)",
    ],
  },
];

const StayCard = ({ item, qty, onChange, onDetails, premium }) => {
  const soldOut = item.soldOut;
  const capped = typeof item.remaining === "number";
  const showAvailability = capped && item.id === "clarity-cottage";
  const low = showAvailability && !soldOut && item.remaining <= 2;
  return (
    <div data-testid={`clarity-stay-${item.id}`} className={`flex flex-col rounded-lg overflow-hidden transition-all duration-300 ${premium ? "ring-2 ring-[#ebc754] shadow-2xl bg-white" : "border border-[#d9c3a8] bg-white shadow-sm"} ${soldOut ? "opacity-60" : ""}`}>
      <button type="button" onClick={onDetails} className="relative h-56 overflow-hidden group">
        <img src={item.image} alt={item.label} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        {premium && <span className="absolute top-4 left-4 bg-[#ebc754] text-[#14110f] text-[11px] font-bold tracking-[0.14em] uppercase px-3 py-1">Most private</span>}
        <span className="absolute bottom-3 right-3 bg-black/55 text-[#f2e5de] text-[11px] font-semibold px-2.5 py-1 inline-flex items-center gap-1"><Images size={13} /> Photos</span>
      </button>
      <div className="p-6 md:p-7 flex flex-col flex-1">
        <p className="text-[#9b3d0a] text-[11px] tracking-[0.16em] uppercase font-bold mb-1">{item.tagline}</p>
        <h3 className="feral-serif text-[#9b3d0a] text-2xl leading-snug mb-1">{item.label}</h3>
        <p className="text-[#9b3d0a] text-xs font-semibold mb-4">{item.nights}</p>
        <div className="flex items-end gap-2 mb-4">
          <span className="feral-serif text-[#9b3d0a] text-4xl leading-none">{gbp(item.price)}</span>
          {item.perPerson && <span className="text-[#6e5646] text-sm mb-1">· {gbp(item.perPerson)} per person</span>}
        </div>
        <p className="text-[#6e5646] text-sm leading-relaxed mb-5">{item.desc || item.blurb || item.longDesc}</p>
        <ul className="grid grid-cols-1 gap-1.5 mb-6">
          {(item.features || []).slice(0, 6).map((f) => (
            <li key={f} className="flex items-start gap-2 text-[#3a3a30] text-sm"><Check size={15} className="text-[#5b8049] mt-0.5 shrink-0" /> {f}</li>
          ))}
        </ul>
        <div className="mt-auto pt-4 border-t border-[#e0d3bf] flex items-center justify-between gap-3">
          {showAvailability && (
            soldOut ? <span className="text-[#9a876f] text-xs font-bold uppercase tracking-wide" data-testid={`clarity-soldout-${item.id}`}>Sold out</span>
            : <span className={`text-xs font-semibold ${low ? "text-[#9b3d0a]" : "text-[#7e6a52]"}`} data-testid={`clarity-left-${item.id}`}>{low ? "Low availability · " : ""}{item.remaining} of {item.capacity} left</span>
          )}
          {soldOut ? (
            <span className="bg-[#d9c3a8] text-[#7a6a58] text-xs font-bold tracking-[0.12em] uppercase px-5 py-3">Sold out</span>
          ) : qty > 0 ? (
            <QuantityStepper value={qty} onChange={onChange} testId={`clarity-qty-${item.id}`} max={capped ? item.remaining : undefined} />
          ) : (
            <button data-testid={`clarity-add-${item.id}`} onClick={() => onChange(1)} className={`px-6 py-3 text-sm font-bold uppercase tracking-[0.1em] inline-flex items-center gap-1.5 transition-colors ${premium ? "bg-[#ebc754] hover:bg-[#e0972f] text-[#14110f]" : "bg-[#9b3d0a] hover:bg-[#7e3208] text-[#ebc754]"}`}>{item.ctaLabel} <Plus size={14} /></button>
          )}
        </div>
      </div>
    </div>
  );
};

const ClarityReset = () => {
  const [event, setEvent] = useState(null);
  const [cart, setCart] = useState(() => { try { return JSON.parse(localStorage.getItem("feral_cart_clarity-reset")) || {}; } catch (e) { return {}; } });
  useEffect(() => {
    try { localStorage.setItem("feral_cart_clarity-reset", JSON.stringify(cart)); } catch (e) { /* ignore */ }
    window.dispatchEvent(new Event("feral-cart-updated"));
  }, [cart]);
  const [detailItem, setDetailItem] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showBasket, setShowBasket] = useState(false);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    axios.get(`${API}/events/${SLUG}`).then((r) => setEvent(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);

  const accommodation = event?.accommodation || [];
  const setQty = (id, qty) => setCart((c) => { const n = { ...c }; if (qty <= 0) delete n[id]; else n[id] = qty; return n; });
  const lines = useMemo(
    () =>
      Object.entries(cart)
        .map(([id, qty]) => {
          const it = accommodation.find((i) => i.id === id);
          return it
            ? {
                id,
                qty,
                name: it.name,
                price: it.price,
                unit: it.unit,
                max: typeof it.remaining === "number" ? it.remaining : undefined,
              }
            : null;
        })
        .filter(Boolean),
    [cart, event] // eslint-disable-line
  );
  const subtotal = lines.reduce((s, l) => s + l.price * l.qty, 0);
  const count = lines.reduce((s, l) => s + l.qty, 0);

  if (showCheckout) {
    return <CheckoutView event={SLUG} items={Object.entries(cart).map(([id, qty]) => ({ id, qty }))} eventName={event?.name || "FERAL Retreat"} dateLine={event?.dateLine} lines={lines} total={subtotal} allowInstallments showDietary bookingConfig={{ askHeard: true, bedChoiceIds: ["clarity-solo", "clarity-two"], guestPlaceIds: accommodation.map((a) => a.id) }} onBack={() => setShowCheckout(false)} />;
  }

  const stayMeta = {
    "clarity-solo": { label: "Solo Room", tagline: "One person · En-suite", ctaLabel: "Book solo" },
    "clarity-two": { label: "Come Together", tagline: "Two people sharing · En-suite", ctaLabel: "Book for two" },
    "clarity-cottage": { label: "Your Own FERAL Hideaway", tagline: "Private cottage", ctaLabel: "Book the cottage" },
  };
  const stayView = accommodation.map((a) => ({ ...a, ...(stayMeta[a.id] || { label: a.name, tagline: "", ctaLabel: "Book" }), premium: a.id === "clarity-cottage", perPerson: a.perPerson ? Math.round(a.price / 2) : null }));

  // Price summary derived from the live room prices so it always matches admin pricing.
  const soloPrice = accommodation.find((a) => a.id === "clarity-solo")?.price;
  const shareRoom = accommodation.find((a) => a.perPerson);
  const sharePerPerson = shareRoom ? Math.round(shareRoom.price / 2) : null;
  const priceSummary = soloPrice
    ? `From ${gbp(soloPrice)} solo${sharePerPerson ? ` · From ${gbp(sharePerPerson)} per person when sharing` : ""}`
    : (event?.priceFrom || "");
  const eventName = event?.name || "FERAL Retreat";

  return (
    <div className="bg-[#f2e5de] min-h-screen">
      <SeoMeta
        title={`${eventName} | 3-Night FERAL Retreat in Anglesey`}
        description={`${eventName} is an intimate 3-night FERAL retreat at Plas Llechylched, Anglesey, 22–25 January 2027, with breathwork, workshops, sound, movement, spa, food and connection.`}
        canonicalPath="/events/clarity-reset"
        image="/retreat-group.webp"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Event",
          name: eventName,
          description: "An intimate 3-night FERAL retreat with breathwork, workshops, sound, movement, spa, food and connection.",
          startDate: "2027-01-22",
          endDate: "2027-01-25",
          eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
          eventStatus: "https://schema.org/EventScheduled",
          location: {
            "@type": "Place",
            name: "Plas Llechylched",
            address: "Anglesey, Wales, United Kingdom",
          },
          image: ["https://feralcollective.co.uk/retreat-group.webp"],
          organizer: {
            "@type": "Organization",
            name: "FERAL Collective",
            url: "https://feralcollective.co.uk/",
          },
          offers: accommodation.map((a) => ({
            "@type": "Offer",
            price: String(a.price),
            priceCurrency: "GBP",
            url: "https://feralcollective.co.uk/events/clarity-reset",
            availability: "https://schema.org/InStock",
          })),
        }}
      />
      <Navbar tone="sunset" />

      {/* HERO — carousel */}
      <section className="relative min-h-[94vh] flex items-end overflow-hidden">
        {HERO_SLIDES.map((src, i) => (
          <img key={src} src={src} alt="A FERAL retreat moment" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === slide ? "opacity-100" : "opacity-0"}`} />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-[#2c6052] via-[#2c6052]/55 to-[#2c6052]/25" />
        <div className="relative z-10 max-w-5xl mx-auto w-full px-6 pb-16 md:pb-24">
          <h1 className="feral-serif text-[#ebc754] text-5xl sm:text-7xl lg:text-8xl leading-[0.92] mb-2">{eventName}</h1>
          {event?.subtitle && <p className="feral-serif text-[#f0dbbe] text-2xl sm:text-3xl mb-6">{event.subtitle}</p>}
          <p className="text-[#f2e5de]/90 tracking-[0.12em] text-sm sm:text-base font-semibold uppercase mb-9">22–25 January · Plas Llechylched, Anglesey</p>
          <button onClick={() => scrollTo("book")} data-testid="clarity-hero-cta" className="inline-flex items-center gap-3 bg-[#ebc754] hover:bg-[#e0972f] text-[#14110f] px-9 py-4 tracking-[0.14em] text-sm font-bold uppercase transition-colors">Book your place <ArrowRight size={17} /></button>
        </div>
        <div className="absolute bottom-6 right-6 z-10 flex gap-2">
          {HERO_SLIDES.map((_, i) => (
            <button key={i} onClick={() => setSlide(i)} aria-label={`Slide ${i + 1}`} className={`h-2 rounded-full transition-all ${i === slide ? "w-6 bg-[#ebc754]" : "w-2 bg-white/50 hover:bg-white/80"}`} />
          ))}
        </div>
      </section>

      {/* INTRO band */}
      <section className="feral-texture py-16 md:py-20 px-6 border-b border-[#d9c3a8]">
        <div className="max-w-5xl mx-auto text-center">
          <p className="feral-serif text-[#9b3d0a] text-3xl md:text-5xl leading-[1.15] mb-8">Are you a rebel?</p>
          <p className="text-[#3a3a30] text-base sm:text-lg leading-relaxed mb-4">Are you a disruptor in your field? Someone who questions the status quo, challenges convention and wants to make a real difference in the world.</p>
          <p className="text-[#9b3d0a] text-lg sm:text-xl font-semibold mb-6">Then this retreat is for you.</p>
          <p className="text-[#3a3a30] text-base sm:text-lg leading-relaxed mb-6">Made specifically for entrepreneurs, creatives and rebels. Three nights immersed in nature, reconnecting with yourself and surrounding yourself with ambitious, like-minded people who dare to do things differently.</p>
          <p className="feral-serif text-[#9b3d0a] text-2xl md:text-3xl mb-8">Come unlock your wildest potential.</p>
          <div className="text-left max-w-2xl mx-auto bg-white/60 border border-[#d9c3a8] rounded-lg p-6 md:p-7 mb-8">
            <p className="text-[#1e231f] font-semibold mb-4">Possible side effects include (but are not limited to):</p>
            <ul className="space-y-2.5">
              {SIDE_EFFECTS.map((s) => (
                <li key={s} className="flex items-start gap-3 text-[#3a3a30] text-sm sm:text-base leading-relaxed">
                  <Check size={17} className="text-[#5b8049] mt-0.5 shrink-0" /> {s}
                </li>
              ))}
            </ul>
          </div>
          <p className="text-[#9b3d0a] text-base sm:text-lg font-bold mb-8">Warning: You might leave questioning everything you thought was possible.</p>
          <p className="text-[#1e231f] text-base font-semibold mb-2">All food, accommodation and retreat experiences included.</p>
          <p className="text-[#6e5646] text-sm">Limited to a small group of like-minded people. Payment plans available.</p>
        </div>
      </section>

      {/* FERAL METHOD */}
      <section className="feral-texture bg-[#2c6052] py-20 md:py-28 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="feral-serif text-[#ebc754] text-4xl md:text-5xl mb-12 text-center">The retreat, the FERAL way</h2>
          <div className="space-y-px">
            {METHOD.map((m, i) => (
              <div key={m.t} className="grid grid-cols-[auto_1fr] gap-5 md:gap-8 py-6 border-t border-white/15">
                <span className="feral-serif text-[#ebc754] text-xl md:text-2xl w-8">0{i + 1}</span>
                <div><h3 className="feral-serif text-[#ebc754] text-2xl md:text-3xl mb-1.5">{m.t}</h3><p className="text-[#f2e5de]/70 text-sm md:text-base leading-relaxed">{m.d}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOSTS */}
      <section className="feral-texture py-20 md:py-28 px-6">
        <div className="max-w-5xl mx-auto">
          <p className="text-[#9b3d0a] tracking-[0.22em] text-xs font-bold uppercase mb-10 text-center">Your hosts</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { name: "Tom", img: "/tom-headshot.png", role: "Vision · Possibility · RISE", copy: "Tom brings vision, possibility, creative problem-solving and a knack for breaking conventional thinking, helping people reconnect with the dreams and ideas they've pushed aside. He leads the RISE element of the retreat." },
              { name: "Calum", img: "/calum.png", role: "Breathwork · Workshops · Reconnection · Rewilding", copy: "Calum is co-host, breathwork facilitator and workshop leader. His work spans breathwork, guided workshops, nervous system regulation, going within, reconnection and rewilding, creating space for deeper emotional and personal exploration." },
            ].map((h) => (
              <div key={h.name} className="bg-white border border-[#d9c3a8] rounded-lg overflow-hidden shadow-sm">
                <div className="aspect-[4/3] overflow-hidden">
                  {h.name === "Tom" ? (
                    <video
                      poster={h.img}
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="auto"
                      data-testid="retreat-sunset-video"
                      className="w-full h-full object-cover [object-position:50%_40%]"
                    >
                      <source src="/retreat-sunset.webm" type="video/webm" />
                      <source src="/retreat-sunset.mp4" type="video/mp4" />
                    </video>
                  ) : (
                    <img src={h.img} alt={h.name} className="w-full h-full object-cover" />
                  )}
                </div>
                <div className="p-6 md:p-7">
                  <h3 className="feral-serif text-[#9b3d0a] text-3xl mb-1">{h.name}</h3>
                  <p className="text-[#9b3d0a] text-xs tracking-[0.14em] uppercase font-bold mb-3">{h.role}</p>
                  <p className="text-[#6e5646] text-sm leading-relaxed">{h.copy}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INCLUDED */}
      <section className="feral-texture bg-[#e2c7b0] py-20 md:py-28 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="feral-serif text-[#9b3d0a] text-4xl md:text-5xl mb-3">Three nights. Everything taken care of.</h2>
          <p className="text-[#6e5646] text-base mb-12">No additional food or programme costs. It's all included.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
            {INCLUDED.map(({ i: Icon, t }) => (
              <div key={t} className="bg-white border border-[#d9c3a8] rounded-lg py-6 px-3 flex flex-col items-center gap-2.5 text-center hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                <Icon size={26} className="text-[#9b3d0a]" strokeWidth={1.6} /><span className="text-[#1e231f] text-xs sm:text-sm font-semibold">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOD + SPA */}
      <section className="feral-texture py-16 md:py-20 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-lg overflow-hidden relative min-h-[360px] flex items-end">
            <video
              poster="/retreat-food.webp"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              data-testid="retreat-lunch-video"
              className="absolute inset-0 w-full h-full object-cover"
            >
              <source src="/retreat-lunch.webm" type="video/webm" />
              <source src="/retreat-lunch.mp4" type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-t from-[#2c6052]/85 to-transparent" />
            <div className="relative z-10 p-7"><p className="text-[#f0dbbe] tracking-[0.2em] text-xs font-bold uppercase mb-2">Gather around the table</p><h3 className="feral-serif text-[#ebc754] text-3xl mb-2">Food, together.</h3><p className="text-[#f2e5de]/80 text-sm leading-relaxed">All meals included and prepared by our private chef. Beautiful, nourishing food served communally, because some of the best ideas, connections and conversations happen around a table with people who get it.</p></div>
          </div>
          <div className="rounded-lg overflow-hidden relative min-h-[360px] flex items-end">
            <video
              poster="/plas-patio-hottub.png"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              data-testid="retreat-spa-video"
              className="absolute inset-0 w-full h-full object-cover"
            >
              <source src="/retreat-spa.webm" type="video/webm" />
              <source src="/retreat-spa.mp4" type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-t from-[#2c6052]/85 to-transparent" />
            <div className="relative z-10 p-7"><p className="text-[#f0dbbe] tracking-[0.2em] text-xs font-bold uppercase mb-2">The rustic spa</p><h3 className="feral-serif text-[#ebc754] text-3xl mb-2">Heat. Cold. Rest. Breathe. Repeat.</h3><p className="text-[#f2e5de]/80 text-sm leading-relaxed">The rustic spa, hot tub and quiet places to land. Communal sea dips too, whenever you fancy them, always optional.</p></div>
          </div>
        </div>
      </section>

      {/* ITINERARY */}
      <section className="feral-texture py-20 md:py-28 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-[#9b3d0a] tracking-[0.22em] text-sm font-semibold uppercase mb-3">The flow of your days</p>
            <h2 className="feral-serif text-[#9b3d0a] text-4xl md:text-5xl mb-3">{eventName} Itinerary</h2>
            <p className="text-[#6e5646] text-sm italic">Example itinerary. Some details may be tweaked.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7">
            {ITINERARY.map((d) => (
              <div key={d.day} data-testid={`clarity-itinerary-${d.day.toLowerCase()}`} className="bg-white border border-[#d9c3a8] rounded-lg p-7 md:p-8 shadow-sm">
                <p className="text-[#9b3d0a] text-xs tracking-[0.16em] uppercase font-bold mb-1">{d.day}</p>
                <h3 className="feral-serif text-[#9b3d0a] text-2xl md:text-3xl mb-5">{d.theme}</h3>
                <ul className="space-y-3">
                  {d.items.map((it, i) => (
                    <li key={i} className="flex items-start gap-3 text-[#3a3a30] text-sm leading-relaxed">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#9b3d0a] shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-center text-[#6e5646] text-base md:text-lg italic max-w-2xl mx-auto mt-12 leading-relaxed">
            Everything is an invitation, not an obligation. Come for what you need. Skip what you don't.
          </p>
        </div>
      </section>

      {/* CHOOSE YOUR STAY / BOOK */}
      <section id="book" className="feral-texture bg-[#e2c7b0] py-16 md:py-20 px-6 scroll-mt-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#9b3d0a] tracking-[0.22em] text-sm font-semibold uppercase mb-3">Choose your stay</p>
            <h2 className="feral-serif text-[#9b3d0a] text-4xl md:text-5xl mb-3">Claim your place</h2>
            <p className="text-[#6e5646] text-sm max-w-xl mx-auto mb-5">A small number of places. All food, accommodation and retreat experiences included.</p>
            <div className="inline-flex items-center gap-3 bg-[#9b3d0a] text-[#ebc754] rounded-full pl-4 pr-5 py-3 shadow-lg" data-testid="retreat-instalments-badge">
              <CreditCard size={22} className="shrink-0" />
              <span className="font-bold text-base sm:text-lg leading-tight">Spread the cost over 4 monthly payments</span>
            </div>
            <p className="text-[#7e6a52] text-xs sm:text-sm mt-3 max-w-md mx-auto">Choose <strong>Pay in full</strong> or <strong>4 monthly instalments</strong> at checkout. The first payment secures your place.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7 items-start">
            {stayView.map((a) => (
              <StayCard key={a.id} item={a} premium={a.premium} qty={cart[a.id] || 0} onChange={(q) => setQty(a.id, q)} onDetails={() => setDetailItem(a)} />
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-28 md:py-40 px-6 overflow-hidden">
        <img src="/retreat-group.webp" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#2c6052]/65" />
        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <h2 className="feral-serif text-[#ebc754] text-4xl md:text-6xl leading-[1.02] mb-6">Step out of the noise.<br />Unlock your wildest potential.</h2>
          <p className="text-[#f2e5de]/85 tracking-[0.1em] text-sm uppercase font-semibold mb-2">22–25 January · Plas Llechylched, Anglesey</p>
          {priceSummary && <p className="text-[#f2e5de]/75 text-base mb-2">{priceSummary}</p>}
          <p className="text-[#ebc754] text-sm mb-9">Only a small number of places available.</p>
          <button onClick={() => scrollTo("book")} className="inline-flex items-center gap-3 bg-[#ebc754] hover:bg-[#e0972f] text-[#14110f] px-10 py-4 tracking-[0.14em] text-sm font-bold uppercase transition-colors">Book the Rebels Reset <ArrowRight size={17} /></button>
          <p className="feral-serif text-[#ebc754] text-2xl mt-10">Be Free, Be FERAL.</p>
        </div>
      </section>

      <Footer />

      <AccommodationModal
        item={detailItem}
        open={detailItem !== null}
        onOpenChange={(o) => !o && setDetailItem(null)}
        qty={detailItem ? cart[detailItem.id] || 0 : 0}
        onChange={(q) => detailItem && setQty(detailItem.id, q)}
        disabled={false}
        onViewBasket={() => {
          setDetailItem(null);
          setShowBasket(true);
        }}
      />

      <BasketModal
        open={showBasket}
        onClose={() => setShowBasket(false)}
        lines={lines}
        subtotal={subtotal}
        onRemove={(id) => setQty(id, 0)}
        onQtyChange={(id, qty) => setQty(id, qty)}
        onCheckout={() => {
          setShowBasket(false);
          setShowCheckout(true);
        }}
      />

      {count > 0 && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:w-auto z-50 pointer-events-none">
          <button
            onClick={() => setShowBasket(true)}
            data-testid="clarity-sticky-checkout"
            className="pointer-events-auto w-full sm:w-auto bg-[#9b3d0a] hover:bg-[#7e3208] text-[#f2e5de] shadow-2xl px-5 py-3.5 flex items-center justify-between sm:justify-start gap-5 transition-all hover:-translate-y-0.5"
          >
            <span className="inline-flex items-center gap-2.5 text-sm font-semibold">
              <span className="relative inline-flex">
                <ShoppingBag size={19} />
                <span className="absolute -top-2 -right-2 min-w-4 h-4 px-1 bg-[#ebc754] text-[#14110f] text-[10px] font-bold flex items-center justify-center">
                  {count}
                </span>
              </span>
              Basket
            </span>
            <span className="font-semibold text-sm">{gbp(subtotal)}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ClarityReset;
